import {useContext, useEffect, useState} from 'react'
import {ConnectorContext, SELECT_CONNECTOR} from '../context/context'
import InputText from '../../../common/InputText'
import TextArea from '../../../common/TextArea'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'
import {useParams} from 'react-router-dom'
import {getRequest, putRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {Col, Flex, Form, Modal, Row, Typography, Upload} from 'antd'
import {CloseCircleOutlined, UploadOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'

const CONNECTOR_CATEGORY_OPTIONS = [
  { value: 'inline', label: 'Inline' },
  { value: 'splice', label: 'Splice' },
  { value: 'fuse', label: 'Fuse' },
  { value: 'module', label: 'Module' },
  { value: 'joint', label: 'Joint' },
  { value: 'high_voltage', label: 'High Voltage' },
  { value: 'ground', label: 'Ground' },
  { value: 'not_electrical', label: 'Not Electrical' },
  { value: 'power_distribution', label: 'Power Distribution' },
  { value: 'connector', label: 'Connector' },
]

const ConnectorFormModal = (props) => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(ConnectorContext)
  const [, navDispatch] = useContext(NavContext)
  const [searchFailed, setSearchFailed] = useState(false)

  const [form] = Form.useForm()
  const [deviceOptions, setDeviceOptions] = useState([])

  const closeFormPopup = () => {
    dispatch({
      type: SELECT_CONNECTOR,
      data: { selected_connector: {}, show_connector_popup: false },
    })
    form.resetFields()
  }

  const getDeviceOptions = async (search = '') => {
    let res = await getRequest(
      `/api/devices/?vehicle=${vehicle}&search=${search}`
    )
    // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
    if (res?.status === false) {
      setSearchFailed(true)
      setDeviceOptions([])
      return
    }
    setSearchFailed(false)
    setDeviceOptions(
      (res.results || []).map((d) => ({
        value: d.id,
        label: d.name,
      }))
    )
  }

  const saveConnector = async (values) => {
    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'connector_side_svg' || key === 'terminal_side_svg') {
        if (value?.[0]?.originFileObj) {
          formData.append(key, value[0].originFileObj)
        }
      } else if (Array.isArray(value) && value.length > 0) {
        value.forEach((item) => {
          if (item && typeof item === 'object' && item.value !== undefined) {
            formData.append(key, item.value)
          } else if (item !== undefined && item !== null) {
            formData.append(key, item)
          }
        })
      } else if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    // Always send name and device as hidden fields
    const selected = state?.selected_connector || {}
    formData.append('name', selected.name || '')
    formData.append('device', selected.device || '')
    formData.append('component', selected.hidden || '')

    let res = await putRequest(
      `/api/connector/save_connector/${selected.id}/`,
      formData
    )

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'connectors.updateSuccess' }),
        },
      })
      props.getConnectorListData()
      closeFormPopup()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'connectors.saveError' }),
        },
      })
    }
  }

  // Initialize form when modal opens
  useEffect(() => {
    if (!state.show_connector_popup) return
    const mod = state?.selected_connector || {}
    form.setFieldsValue({
      color: mod.color || null,
      category: mod.category
        ? { value: mod.category, label: CONNECTOR_CATEGORY_OPTIONS.find((o) => o.value === mod.category)?.label || mod.category }
        : null,
      cavities: mod.cavities || null,
      icon: mod.icon || null,
      description: mod.description || null,
      device_data: mod.device_data
        ? { value: mod.device_data.id, label: mod.device_data.name }
        : null,
      alias: mod.alias || null,
      postfix: mod.postfix || null,
      fpn: mod.fpn || null,
      customer_part_number: mod.customer_part_number || null,
      svg_group_reference: mod.svg_group_reference || null,
    })
    if (mod.device_data) {
      setDeviceOptions([{ value: mod.device_data.id, label: mod.device_data.name }])
    } else {
      getDeviceOptions('')
    }
  }, [state.show_connector_popup])

  return (
    <Modal
      open={state.show_connector_popup}
      title={intl.formatMessage({ id: 'connectors.connectorDetails' })}
      width="60%"
      className="connectors-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form
          id="connector-form"
          name="connector-form"
          form={form}
          layout="vertical"
          onFinish={saveConnector}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <InputText
                name="color"
                label={intl.formatMessage({ id: 'connectors.color' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <SelectInput
                name="category"
                label={intl.formatMessage({ id: 'connectors.category' })}
                options={CONNECTOR_CATEGORY_OPTIONS}
                isRequired={false}
                allowClear
              />
            </Col>

            <Col span={12}>
              <InputText
                name="cavities"
                label={intl.formatMessage({ id: 'connectors.cavities' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="icon"
                label={intl.formatMessage({ id: 'connectors.icon' })}
                isRequired={false}
              />
            </Col>

            <Col span={24}>
              <TextArea
                name="description"
                label={intl.formatMessage({ id: 'connectors.description' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <SelectInput
                name="device_data"
                label={intl.formatMessage({ id: 'connectors.device' })}
                options={deviceOptions}
                notFoundContent={
                  searchFailed ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
                isRequired={false}
                allowClear
                onInputChange={(value) => {
                  if (value) {
                    getDeviceOptions(value)
                  }
                }}
                onMenuOpen={() => {
                  if (deviceOptions?.length < 1) {
                    getDeviceOptions('')
                  }
                }}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="alias"
                label={intl.formatMessage({ id: 'connectors.alias' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="postfix"
                label={intl.formatMessage({ id: 'connectors.postfix' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="fpn"
                label={intl.formatMessage({ id: 'connectors.partNumber' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="customer_part_number"
                label={intl.formatMessage({ id: 'connectors.customerPartNumber' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="svg_group_reference"
                label={intl.formatMessage({ id: 'connectors.svgGroupReference' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <Form.Item
                name="connector_side_svg"
                label={intl.formatMessage({ id: 'connectors.connectorSideSvg' })}
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList || []}
              >
                <Upload
                  maxCount={1}
                  beforeUpload={() => false}
                  accept=".svg"
                >
                  <Button icon={<UploadOutlined />}>
                    {intl.formatMessage({ id: 'connectors.upload' })}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="terminal_side_svg"
                label={intl.formatMessage({ id: 'connectors.terminalSideSvg' })}
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList || []}
              >
                <Upload
                  maxCount={1}
                  beforeUpload={() => false}
                  accept=".svg"
                >
                  <Button icon={<UploadOutlined />}>
                    {intl.formatMessage({ id: 'connectors.upload' })}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>

            {state?.selected_connector?.svg && (
              <Col span={24}>
                <Flex align="center" gap="small">
                  <Typography.Text strong>
                    {intl.formatMessage({ id: 'connectors.currentSvg' })}:
                  </Typography.Text>
                  <Typography.Text>
                    {state?.selected_connector?.name}.svg
                  </Typography.Text>
                  <Button
                    type="text"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => {
                      const updated = { ...state.selected_connector, svg: null }
                      dispatch({
                        type: SELECT_CONNECTOR,
                        data: {
                          selected_connector: updated,
                          show_connector_popup: true,
                        },
                      })
                    }}
                  />
                </Flex>
              </Col>
            )}

            <Col span={24}>
              <Flex justify="end">
                <Button htmlType="submit" type="primary">
                  {intl.formatMessage({ id: 'connectors.save' })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default ConnectorFormModal
